import { CheckOutlined, RollbackOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import { Alert, Button, Card, message, Modal, Spin, Tooltip, Typography } from 'antd';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

import { apiUrl } from '../../common/url';

interface TableBackupInfo {
  backup_date: string;
  row_count: number;
}

interface RestorePreviousOriginalTableCardProps {
  dataCollName: string | undefined;
  client: { id: string; token: string; permissions: string[] };
  style?: React.CSSProperties;
}

// TODO: WIP, as backup functionality was moved out of scope
// This ard should be only for backup and restore
const RestorePreviousOriginalTableCard: React.FC<RestorePreviousOriginalTableCardProps> = ({
  dataCollName,
  client,
  style,
}) => {
  const [loading, setLoading] = useState(false);
  const [backupInfo, setBackupInfo] = useState<TableBackupInfo | null>(null);

  useEffect(() => {
    if (!dataCollName) return;

    axios
      .get(`${apiUrl}/b2zero/${client.id}/get_table_backup_info`, {
        params: { table_name: `${dataCollName}_basis` },
        headers: { Authorization: `Bearer ${client.token}` },
      })
      .then((response) => {
        setBackupInfo(response.data);
      })
      .catch((error) => {
        console.error('Error fetching backup info:', error);
        Sentry.captureException(error);

        // message.warning('Using mock backup info');
        // setBackupInfo({
        //   backup_date: '2024-01-01',
        //   row_count: 100,
        // });
      });
  }, [dataCollName, client.id, client.token]);

  const handleRestorePreviousOriginalTable = () => {
    if (!dataCollName) return;

    Modal.confirm({
      title: 'Tabelle wiederherstellen',
      content: (
        <div>
          <p style={{ color: '#ff4d4f', marginBottom: 16 }}>
            ⚠️ Warnung: Diese Aktion überschreibt die aktuelle Tabelle mit dem Backup!
          </p>
          <p>{`Möchten Sie die Tabelle "${dataCollName}" mit dem Backup wiederherstellen?`}</p>
          {backupInfo && (
            <p>
              Backup vom: {new Date(backupInfo.backup_date).toLocaleString()}
              <br />
              Anzahl Zeilen: {backupInfo.row_count}
            </p>
          )}
        </div>
      ),
      okText: 'Ja, wiederherstellen',
      cancelText: 'Abbrechen',
      okButtonProps: { danger: true },
      onOk: () => {
        setLoading(true);
        return axios
          .post(
            `${apiUrl}/b2zero/${client.id}/restore_table_from_backup`,
            {
              table_name: `${dataCollName}_basis`,
            },
            {
              headers: { Authorization: `Bearer ${client.token}` },
            }
          )
          .then(() => {
            message.success('Tabelle wurde erfolgreich wiederhergestellt');
          })
          .catch((error) => {
            message.error('Fehler beim Wiederherstellen der Tabelle: ' + error.message);
            Sentry.captureException(error);
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };

  const handleReplaceOriginalTable = () => {
    if (!dataCollName) return;

    Modal.confirm({
      title: 'Tabelle ersetzen',
      content: (
        <div>
          <p style={{ color: '#ff4d4f', marginBottom: 16 }}>
            ⚠️ Warnung: Diese Aktion kann nicht rückgängig gemacht werden!
          </p>
          <p>{`Die Tabelle "${dataCollName}_basis" wird durch "${dataCollName}_basis_upload" ersetzt.`}</p>
          <p>
            Die aktuelle Tabelle wird automatisch als Backup gespeichert und kann bei Bedarf
            wiederhergestellt werden. Wiederherstellung ist nur möglich bis zum nächsten Ersetzen
            der Tabelle.
          </p>
        </div>
      ),
      okText: 'Ja, Tabelle ersetzen',
      cancelText: 'Abbrechen',
      okButtonProps: { danger: true },
      onOk: () => {
        setLoading(true);
        return axios
          .post(
            `${apiUrl}/b2zero/${client.id}/replace_table_with_upload`,
            {
              table_name: `${dataCollName}_basis`,
            },
            {
              headers: { Authorization: `Bearer ${client.token}` },
            }
          )
          .then(() => {
            message.success('Tabelle wurde erfolgreich ersetzt');
          })
          .catch((error) => {
            message.error('Fehler beim Ersetzen der Tabelle: ' + error.message);
            Sentry.captureException(error);
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };

  if (!backupInfo) return null;

  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Title level={3}>
            {dataCollName} ersetzen oder zurücksetzen
            {loading && <Spin style={{ marginLeft: 12 }} />}
          </Typography.Title>
        </div>
      }
      loading={loading}
      style={{ marginBottom: 48, ...style }}
    >
      <Alert
        message={`Vorsicht: Diese Aktionen überschreiben die aktuelle ${dataCollName} Tabelle`}
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Alert
        message={`Hier kann die aktuelle ${dataCollName}_basis Tabelle durch hochgeladene Stammdatentabelle ersetzt werden.`}
        description={`Außerdem kann in diesem Bereich die aktuelle Tabelle auf die vorige version zurückgesetzt werden.`}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      Work in Progress, UI and wordings will be updated
      <div style={{ marginBottom: 16 }}>
        <p>
          <strong>Backup Details:</strong>
        </p>
        <p>Datum: {new Date(backupInfo.backup_date).toLocaleString()}</p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button
          type="default"
          danger
          icon={<RollbackOutlined />}
          onClick={handleRestorePreviousOriginalTable}
          disabled={loading}
        >
          Vorherige Version wiederherstellen
        </Button>
        <Tooltip title="Tabelle durch hochgeladene Tabelle ersetzen">
          <Button
            type="primary"
            danger
            icon={<CheckOutlined />}
            onClick={handleReplaceOriginalTable}
            disabled={loading}
          >
            Tabelle ersetzen
          </Button>
        </Tooltip>
      </div>
    </Card>
  );
};

export default RestorePreviousOriginalTableCard;
